/**
 * InterpretView - 多角色解读组件
 */

import { useState } from 'react'
import { useLanguage } from '../hooks/useLanguage'

interface RoleInterpretation {
  one_line_take?: string
  why_it_matters?: string
  opportunity?: string
  risk?: string
  what_to_watch?: string
  core_meaning?: string
  impact_on_builders?: string
  potential_opportunities?: string
  needs_observation?: string
  background_structure?: string
  deep_meaning?: string
  evidence_signals?: string[]
  questions_to_verify?: string[]
  business_opportunity?: string
  strategic_impact?: string
  execution_points?: string[]
  risk_warning?: string
}

interface Interpretations {
  trader?: RoleInterpretation
  builder?: RoleInterpretation
  researcher?: RoleInterpretation
  founder?: RoleInterpretation
}

interface InterpretViewProps {
  interpretations: Interpretations
}

const ROLE_CONFIG = {
  trader: {
    name: '交易者',
    nameEn: 'Trader',
    icon: '📊',
    color: 'border-blue-500',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
  },
  builder: {
    name: '建设者',
    nameEn: 'Builder',
    icon: '🏗️',
    color: 'border-green-500',
    bgColor: 'bg-green-50 dark:bg-green-900/30',
  },
  researcher: {
    name: '研究者',
    nameEn: 'Researcher',
    icon: '🔬',
    color: 'border-purple-500',
    bgColor: 'bg-purple-50 dark:bg-purple-900/30',
  },
  founder: {
    name: '创业者',
    nameEn: 'Founder',
    icon: '💼',
    color: 'border-orange-500',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30',
  },
}

export function InterpretView({ interpretations }: InterpretViewProps) {
  const [expanded, setExpanded] = useState<string | null>(null)
  const { t } = useLanguage()

  const roles = Object.keys(interpretations) as Array<keyof Interpretations>

  if (roles.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      {roles.map((role) => {
        const config = ROLE_CONFIG[role]
        const data = interpretations[role]
        if (!data) return null

        const isExpanded = expanded === role

        return (
          <div
            key={role}
            className={`border-l-4 ${config.color} ${config.bgColor} rounded-r-lg overflow-hidden`}
          >
            {/* 头部 */}
            <button
              onClick={() => setExpanded(isExpanded ? null : role)}
              className="w-full flex items-center justify-between p-3 text-left"
            >
              <span className="font-medium">
                {config.icon} {t(config.name, config.nameEn)}
              </span>
              <span className="text-gray-500">{isExpanded ? '▼' : '▶'}</span>
            </button>

            {/* 展开内容 */}
            {isExpanded && (
              <div className="px-3 pb-3 text-sm space-y-2">
                {/* Trader */}
                {role === 'trader' && (
                  <>
                    {data.one_line_take && (
                      <p><strong>{t('一句话判断:', 'One-line take:')}</strong> {data.one_line_take}</p>
                    )}
                    {data.why_it_matters && (
                      <p><strong>{t('为什么重要:', 'Why it matters:')}</strong> {data.why_it_matters}</p>
                    )}
                    {data.opportunity && (
                      <p><strong>{t('机会:', 'Opportunity:')}</strong> {data.opportunity}</p>
                    )}
                    {data.risk && (
                      <p className="text-red-600"><strong>{t('风险:', 'Risk:')}</strong> {data.risk}</p>
                    )}
                    {data.what_to_watch && (
                      <p><strong>{t('关注点:', 'What to watch:')}</strong> {data.what_to_watch}</p>
                    )}
                  </>
                )}

                {/* Builder */}
                {role === 'builder' && (
                  <>
                    {data.core_meaning && (
                      <p><strong>{t('核心含义:', 'Core meaning:')}</strong> {data.core_meaning}</p>
                    )}
                    {data.impact_on_builders && (
                      <p><strong>{t('对建设者的影响:', 'Impact on builders:')}</strong> {data.impact_on_builders}</p>
                    )}
                    {data.potential_opportunities && (
                      <p><strong>{t('潜在机会:', 'Potential opportunities:')}</strong> {data.potential_opportunities}</p>
                    )}
                    {data.needs_observation && (
                      <p><strong>{t('需要观察:', 'Needs observation:')}</strong> {data.needs_observation}</p>
                    )}
                  </>
                )}

                {/* Researcher */}
                {role === 'researcher' && (
                  <>
                    {data.background_structure && (
                      <p><strong>{t('背景结构:', 'Background structure:')}</strong> {data.background_structure}</p>
                    )}
                    {data.deep_meaning && (
                      <p><strong>{t('深层含义:', 'Deeper meaning:')}</strong> {data.deep_meaning}</p>
                    )}
                    {data.evidence_signals && (
                      <p><strong>{t('证据信号:', 'Evidence signals:')}</strong> {data.evidence_signals.join(', ')}</p>
                    )}
                    {data.questions_to_verify && (
                      <p><strong>{t('待验证问题:', 'Questions to verify:')}</strong> {data.questions_to_verify.join(', ')}</p>
                    )}
                  </>
                )}

                {/* Founder */}
                {role === 'founder' && (
                  <>
                    {data.business_opportunity && (
                      <p><strong>{t('商业机会:', 'Business opportunity:')}</strong> {data.business_opportunity}</p>
                    )}
                    {data.strategic_impact && (
                      <p><strong>{t('战略影响:', 'Strategic impact:')}</strong> {data.strategic_impact}</p>
                    )}
                    {data.execution_points && (
                      <p><strong>{t('执行要点:', 'Execution points:')}</strong> {data.execution_points.join('; ')}</p>
                    )}
                    {data.risk_warning && (
                      <p className="text-red-600"><strong>{t('风险提示:', 'Risk warning:')}</strong> {data.risk_warning}</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
